import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/clCommunityData';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">该文章不存在或已被删除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-all text-sm font-medium cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author.name,
    },
    datePublished: article.publishedAt,
    articleSection: article.categoryName,
    keywords: article.tags.join(', '),
    url: `https://clshequ.com/article/${article.id}`,
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <Navbar />
      <main className="pt-16">
        <article>
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-3xl mx-auto px-4 md:px-6 py-8">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: article.categoryName, href: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />
            </div>
          </div>

          <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-10">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              {article.isPinned && (
                <span className="bg-primary-500 text-white text-xs font-medium px-2.5 py-1 rounded-full">置顶</span>
              )}
              <Link
                to={`/category/${article.category}`}
                className="bg-secondary-100 text-secondary-700 text-xs font-medium px-2.5 py-1 rounded-full cursor-pointer hover:bg-secondary-200 transition-colors"
              >
                {article.categoryName}
              </Link>
            </div>

            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 mb-8 flex-wrap">
              <div className="flex items-center gap-2">
                <img
                  src={article.author.avatar}
                  alt={article.author.name}
                  className="w-9 h-9 rounded-full object-cover"
                />
                <div>
                  <p className="text-sm font-medium text-foreground-800">{article.author.name}</p>
                  <p className="text-xs text-foreground-500">发布于 {article.publishedAt}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs text-foreground-500 ml-auto">
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views} 阅读
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-chat-3-line"></i>
                  {article.comments} 评论
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-thumb-up-line"></i>
                  {article.likes} 赞
                </span>
              </div>
            </div>

            <div className="relative h-64 md:h-80 lg:h-96 rounded-xl overflow-hidden mb-8">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover object-top"
                title={`${article.title} - CL社区`}
              />
            </div>

            <div
              className="prose prose-lg max-w-none text-foreground-800 leading-relaxed article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex items-center gap-2 mt-8 pt-6 border-t border-background-200/70 flex-wrap">
              <span className="text-xs text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs bg-background-100 text-foreground-600 px-2.5 py-1 rounded-full border border-background-200/70"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="flex items-center justify-between mt-6 pt-6 border-t border-background-200/70">
              <div className="flex items-center gap-3">
                <button className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-background-200 text-sm text-foreground-600 hover:bg-background-100 transition-all cursor-pointer">
                  <i className="ri-thumb-up-line"></i>
                  赞 ({article.likes})
                </button>
                <button className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-background-200 text-sm text-foreground-600 hover:bg-background-100 transition-all cursor-pointer">
                  <i className="ri-bookmark-line"></i>
                  收藏
                </button>
                <button className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-background-200 text-sm text-foreground-600 hover:bg-background-100 transition-all cursor-pointer">
                  <i className="ri-share-forward-line"></i>
                  分享
                </button>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-12 md:py-14">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden group cursor-pointer hover:-translate-y-0.5 transition-all duration-300"
                  >
                    <div className="relative h-36 overflow-hidden">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        title={`${ra.title} - CL社区`}
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading font-semibold text-foreground-900 text-sm line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-2">{ra.author.name} · {ra.publishedAt}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}